import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Camera, Image, User, Settings, FileText,
  ChevronRight, LogOut, Save, Send, Clock, Check, X, AlertCircle, Bell, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { MediaGallery } from '../../components/MediaGallery';
import { NotificationsPanel } from '../../components/NotificationBell';
import { UserInbox } from '../../components/Inbox';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Borrador', icon: FileText },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'En Revisión', icon: Clock },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Aprobado', icon: Check },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rechazado', icon: X },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Cambios Solicitados', icon: AlertCircle },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${c.bg} ${c.text}`}>
      <c.icon size={14} />
      {c.label}
    </span>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'profile', icon: User, label: 'Mi Perfil' },
    { id: 'gallery', icon: Image, label: 'Galería' },
    { id: 'messages', icon: MessageCircle, label: 'Mensajes' },
    { id: 'notifications', icon: Bell, label: 'Notificaciones' },
    { id: 'stats', icon: LayoutDashboard, label: 'Estadísticas' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img src="/gs-agency-logo.png" alt="GS Agency" className="h-10 w-auto object-contain" />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-pink-400 text-xs uppercase tracking-wider px-4 mb-4 flex items-center gap-2">
          <Camera size={12} /> Panel de Modelo
        </p>
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all mb-1 ${
              activeTab === item.id
                ? 'bg-pink-500/10 text-pink-400'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <item.icon size={18} />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5">
          <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
            <Camera size={18} className="text-pink-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-white/40 text-xs">Modelo</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 mt-2 text-sm transition-colors"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </div>
    </aside>
  );
};

// Profile Tab
const ProfileTab = ({ token }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: { es: '', en: '' },
    bio: { es: '', en: '' },
    physical_attributes: {
      height: '',
      measurements: '',
      eye_color: '',
      hair_color: '',
      shoe_size: ''
    },
    categories: [],
    skills: [],
    experience: [],
    city: '',
    country: '',
    social_links: {},
    cover_image: '',
    gallery: []
  });

  const categoryOptions = ['Fashion', 'Commercial', 'Editorial', 'Runway', 'Fitness', 'Glamour', 'Promotional', 'Parts Model'];
  const skillOptions = ['Posing', 'Acting', 'Dancing', 'Sports', 'Yoga', 'Martial Arts', 'Swimming', 'Driving'];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/model/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          setProfile(response.data);
          setFormData({
            name: response.data.name || { es: '', en: '' },
            bio: response.data.bio || { es: '', en: '' },
            physical_attributes: response.data.physical_attributes || {},
            categories: response.data.categories || [],
            skills: response.data.skills || [],
            experience: response.data.experience || [],
            city: response.data.city || '',
            country: response.data.country || '',
            social_links: response.data.social_links || {},
            cover_image: response.data.cover_image || '',
            gallery: response.data.gallery || []
          });
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching profile:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      const endpoint = profile ? `${API}/model/profile` : `${API}/model/profile`;
      const method = profile ? 'put' : 'post';
      
      await axios[method](endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (submit) {
        await axios.post(`${API}/model/profile/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Perfil enviado para revisión');
      } else {
        toast.success('Perfil guardado');
      }
      
      // Refresh profile
      const response = await axios.get(`${API}/model/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      toast.error('Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const lastReview = profile?.review_notes?.[profile.review_notes.length - 1];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Mi Perfil</h1>
          <p className="text-white/50">Gestiona tu información profesional</p>
        </div>
        {profile && <StatusBadge status={profile.status} />}
      </div>

      {/* Review Notes Alert */}
      {lastReview && (profile.status === 'rejected' || profile.status === 'changes_requested') && (
        <div className={`p-4 rounded-xl border ${
          profile.status === 'rejected' 
            ? 'bg-red-500/10 border-red-500/30' 
            : 'bg-orange-500/10 border-orange-500/30'
        }`}>
          <div className="flex items-start gap-3">
            <AlertCircle className={profile.status === 'rejected' ? 'text-red-400' : 'text-orange-400'} size={20} />
            <div>
              <p className="text-white font-medium mb-1">
                {profile.status === 'rejected' ? 'Perfil Rechazado' : 'Cambios Solicitados'}
              </p>
              <p className="text-white/70 text-sm">{lastReview.notes}</p>
              <p className="text-white/40 text-xs mt-2">
                Por {lastReview.reviewer_name} • {new Date(lastReview.timestamp).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-6">Información Básica</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-sm block mb-2">Nombre Artístico (Español)</label>
                <Input
                  value={formData.name.es}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, es: e.target.value } }))}
                  placeholder="Tu nombre artístico"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Nombre Artístico (Inglés)</label>
                <Input
                  value={formData.name.en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                  placeholder="Your stage name"
                  className="input-premium"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-white/50 text-sm block mb-2">Ciudad</label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Ciudad de México"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">País</label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="México"
                  className="input-premium"
                />
              </div>
            </div>
          </div>

          {/* Physical Attributes */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-6">Atributos Físicos</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="text-white/50 text-sm block mb-2">Altura</label>
                <Input
                  value={formData.physical_attributes.height || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    physical_attributes: { ...prev.physical_attributes, height: e.target.value } 
                  }))}
                  placeholder="1.75m"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Medidas</label>
                <Input
                  value={formData.physical_attributes.measurements || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    physical_attributes: { ...prev.physical_attributes, measurements: e.target.value } 
                  }))}
                  placeholder="90-60-90"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Color de Ojos</label>
                <Input
                  value={formData.physical_attributes.eye_color || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    physical_attributes: { ...prev.physical_attributes, eye_color: e.target.value } 
                  }))}
                  placeholder="Café"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Color de Cabello</label>
                <Input
                  value={formData.physical_attributes.hair_color || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    physical_attributes: { ...prev.physical_attributes, hair_color: e.target.value } 
                  }))}
                  placeholder="Negro"
                  className="input-premium"
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Talla de Calzado</label>
                <Input
                  value={formData.physical_attributes.shoe_size || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    physical_attributes: { ...prev.physical_attributes, shoe_size: e.target.value } 
                  }))}
                  placeholder="38"
                  className="input-premium"
                />
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-6">Biografía</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-white/50 text-sm block mb-2">Español</label>
                <Textarea
                  value={formData.bio.es}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: { ...prev.bio, es: e.target.value } }))}
                  placeholder="Cuéntanos sobre ti, tu experiencia y estilo..."
                  className="input-premium"
                  rows={4}
                />
              </div>
              <div>
                <label className="text-white/50 text-sm block mb-2">Inglés</label>
                <Textarea
                  value={formData.bio.en}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: { ...prev.bio, en: e.target.value } }))}
                  placeholder="Tell us about yourself, your experience and style..."
                  className="input-premium"
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-6">Categorías</h2>
            <div className="flex flex-wrap gap-2">
              {categoryOptions.map((cat) => (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    formData.categories.includes(cat)
                      ? 'bg-pink-500 text-white'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-6">Habilidades</h2>
            <div className="flex flex-wrap gap-2">
              {skillOptions.map((skill) => (
                <button
                  key={skill}
                  onClick={() => toggleSkill(skill)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    formData.skills.includes(skill)
                      ? 'bg-pink-500 text-white'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar - Cover Image & Actions */}
        <div className="space-y-6">
          {/* Cover Image */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-4">Foto Principal</h2>
            {formData.cover_image ? (
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-4">
                <img src={formData.cover_image} alt="Cover" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-[3/4] rounded-lg bg-white/5 flex items-center justify-center mb-4">
                <Camera size={32} className="text-white/20" />
              </div>
            )}
            <Input
              value={formData.cover_image}
              onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))}
              placeholder="URL de la imagen"
              className="input-premium text-sm"
            />
          </div>

          {/* Social Links */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-4">Redes Sociales</h2>
            <div className="space-y-3">
              <Input
                value={formData.social_links.instagram || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  social_links: { ...prev.social_links, instagram: e.target.value } 
                }))}
                placeholder="Instagram username"
                className="input-premium text-sm"
              />
              <Input
                value={formData.social_links.tiktok || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  social_links: { ...prev.social_links, tiktok: e.target.value } 
                }))}
                placeholder="TikTok username"
                className="input-premium text-sm"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="glass-dark rounded-xl p-6 border border-white/5">
            <h2 className="text-white font-display text-lg mb-4">Acciones</h2>
            <div className="space-y-3">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="w-full btn-secondary flex items-center justify-center gap-2"
              >
                <Save size={18} />
                {saving ? 'Guardando...' : 'Guardar Borrador'}
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving || profile?.status === 'pending'}
                className="w-full btn-gold flex items-center justify-center gap-2"
              >
                <Send size={18} />
                {profile?.status === 'pending' ? 'En Revisión' : 'Enviar a Revisión'}
              </button>
            </div>
            {profile?.status === 'pending' && (
              <p className="text-white/40 text-xs text-center mt-3">
                Tu perfil está siendo revisado por el equipo
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Gallery Tab
const GalleryTab = ({ token }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/model/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const handleGalleryChange = async (newGallery) => {
    try {
      await axios.put(`${API}/model/profile`, { gallery: newGallery }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(prev => ({ ...prev, gallery: newGallery }));
      toast.success('Galería actualizada');
    } catch (error) {
      toast.error('Error al actualizar galería');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Mi Galería</h1>
        <p className="text-white/50">Gestiona tus fotos profesionales</p>
      </div>

      <div className="glass-dark rounded-xl p-6 border border-white/5">
        <MediaGallery
          images={profile?.gallery || []}
          onChange={handleGalleryChange}
          maxImages={20}
        />
      </div>

      <div className="glass-dark rounded-xl p-6 border border-pink-500/20">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
            <Camera size={20} className="text-pink-400" />
          </div>
          <div>
            <h3 className="text-white font-medium mb-1">Tips para tu Galería</h3>
            <ul className="text-white/50 text-sm space-y-1">
              <li>• Incluye fotos de cuerpo completo, medio cuerpo y close-up</li>
              <li>• Usa fondos neutros para destacar tu imagen</li>
              <li>• Muestra versatilidad con diferentes looks y estilos</li>
              <li>• Las fotos profesionales aumentan tus oportunidades</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stats Tab
const StatsTab = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-white mb-2">Estadísticas</h1>
        <p className="text-white/50">Métricas de tu perfil</p>
      </div>

      <div className="glass-dark rounded-xl p-12 text-center border border-white/5">
        <LayoutDashboard size={48} className="text-white/20 mx-auto mb-4" />
        <h2 className="text-white font-display text-xl mb-2">Próximamente</h2>
        <p className="text-white/50">Estadísticas de visualizaciones y contactos</p>
      </div>
    </div>
  );
};

// Main ModelDashboard Component
export default function ModelDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!user || user.role !== 'model') {
      navigate('/login');
    }
  }, [user, navigate]);

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab token={token} />;
      case 'gallery':
        return <GalleryTab token={token} />;
      case 'messages':
        return <UserInbox />;
      case 'notifications':
        return <NotificationsPanel />;
      case 'stats':
        return <StatsTab />;
      default:
        return <ProfileTab token={token} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="ml-64 p-8">
        {renderContent()}
      </main>
    </div>
  );
}
